import { useCallback } from 'react';
import { useAppSelector } from 'app/hooks';
import {
  selectIsUserAgent,
  selectSessionLastVisitedAt,
} from 'services/session/sessionSelectors';
import { ONBOARDING_MODAL_VIEWED_KEY } from './constants';

/**
 * Eligibility gate for the dashboard onboarding tour.
 *
 * Ported from Angular verbatim (`dashboard-main.component.ts:319`):
 *
 *   state.lastVisitedAt === null
 *   && sessionStorage.getItem('onboardingModalViewed') === null
 *
 * plus Angular's outer guard `state.agentId === null`
 * (`dashboard-main.component.ts:311`).
 *
 * Three deliberate divergences from Angular:
 *
 *  1. Angular also guards on `lastAction !== 'REFRESH_TOKEN'` and
 *     `!== 'CREATE_AP_SESSION'`. React's session slice has no `lastAction`
 *     field, and one is NOT invented here: Angular needs it because its gate
 *     lives in a store subscription that re-fires on every session action,
 *     whereas this gate is read once per mount and `markOpened()` makes
 *     re-entry idempotent.
 *  2. Angular's `state.userLoggedIn` check is implicit — the dashboard route
 *     is behind auth, so an unauthenticated user never reaches this hook.
 *  3. Angular also requires `state.migratedAt === null`, routing migrated users
 *     to `MigratedOnboardingModalComponent` instead. That condition is dropped
 *     here by product decision (2026-08-21), so **migrated users now see the
 *     tour like anyone else on their first visit**. Nothing in React consumes
 *     `migratedAt` for this flow, and the migrated-user modal was never
 *     migrated. Re-add both the field and the condition if that changes.
 *
 * `lastVisitedAt` comes from the SESSION slice, captured from the sign-in
 * response's `profile` — deliberately not from `user.account`, which is fed by
 * `GET api/client/users/{id}`:
 *
 *  - That endpoint returns `lastVisitedAt` AFTER the backend stamps this login,
 *    so it reads as a timestamp of "now" even for a brand-new user and the gate
 *    would never open.
 *  - It also resolves after sign-in, so the value flips mid-boot: the gate has
 *    two consumers (`SessionModalSequence`, `useDashboardTour`) and they would
 *    disagree depending on when each first rendered.
 *
 * Angular keeps the identical separation: `session.reducer.ts:49` fills the
 * session store from `profile`, while `GET /client/users/{id}` feeds a separate
 * `user` store that the tour gate never reads.
 *
 * Note the falsy check rather than `=== null`: it covers `null` (not yet known
 * / never visited) and `''` alike.
 */
function useDashboardTourGate() {
  const lastVisitedAt = useAppSelector(selectSessionLastVisitedAt);
  const isAgent = useAppSelector(selectIsUserAgent);

  const isFirstVisit = !lastVisitedAt;
  const alreadyViewedThisSession =
    sessionStorage.getItem(ONBOARDING_MODAL_VIEWED_KEY) !== null;

  const isEligible = isFirstVisit && !isAgent && !alreadyViewedThisSession;

  /**
   * Written at OPEN time, not on close — matching Angular, which sets the key
   * on the same tick it opens the dialog. Skip, "Don't show again", Finish and
   * an accidental reload therefore all behave identically: the tour will not
   * reopen for the rest of this browser session.
   */
  const markOpened = useCallback(() => {
    sessionStorage.setItem(ONBOARDING_MODAL_VIEWED_KEY, 'true');
  }, []);
  return { isEligible, markOpened };
}

export default useDashboardTourGate;
